/* eslint-disable react-hooks/exhaustive-deps */
import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addScreeningFinding,
  editCrimeReferences2,
} from "services/incidentService";
import { CrimeReference, FILE_URL, IncidentDecisionType, Roles } from "types";
import { LoadingIcon } from "utils/constant";
import {
  errorHandler,
  errorHandlerOnForm,
  hasRole,
  splitCamelCase,
} from "utils/helper";

type Props = {
  incident: CrimeReference;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  setChangeTab: React.Dispatch<React.SetStateAction<string>>;
};

const CrimeFinalInformation: FC<Props> = ({
  incident,
  setRefetch,
  setChangeTab,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [activeForm, setActiveForm] = useState<"finding" | "decision" | null>(
    null,
  );
  const [isOpenReject, setIsOpenReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [openFindingIndex, setOpenFindingIndex] = useState<number | null>(null);
  const [formState, setFormState] = useState<{
    decision_type: any;
    decision_date: string;
    decision_details: string;
    description: string;
  }>({
    decision_type: "pending",
    decision_date: "",
    decision_details: "",
    description: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const hasFinding =
    incident?.screening_team_findings &&
    incident?.screening_team_findings?.length > 0;
  const hasFinalDecision = incident?.decision_type?.toString() !== "pending";

  useEffect(() => {
    // Initialize form state with existing data
    if (incident) {
      setFormState({
        decision_type: incident?.decision_type || "pending",
        decision_date: incident?.decision_date
          ? new Date(incident.decision_date).toISOString().split("T")[0]
          : "",
        decision_details: incident?.decision_details || "",
        description: "",
      });

      setRejectReason(incident?.mesage_for_screeningteam || "");
    }
  }, [incident]);

  if (
    hasRole(loginData?.roles, [Roles.prosecutionDirector]) &&
    incident?.decision_type?.toString() === "pending"
  ) {
    return null;
  }

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleFile = (file: File | null) => {
    setFile(file);
  };

  const handleSubmitFinding = async () => {
    if (!file && !hasFinding) {
      showTooltip(t("file_is_required"), "error");
      return;
    }

    setLoading(true);
    try {
      await addScreeningFinding({
        crime_reference: incident.uu_id,
        file_uploads: file,
        description: formState.description,
        uu_id: 0,
      });

      showTooltip(
        hasFinding
          ? t("finding_updated_successfully")
          : t("finding_added_successfully"),
        "success",
      );
      setRefetch((prev) => !prev);
      setActiveForm(null);
      setFormState((prev) => ({ ...prev, description: "" }));
      setFile(null);
    } catch (error: any) {
      showTooltip(
        error?.response?.data?.detail || errorHandlerOnForm(error),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDecision = async () => {
    if (!incident?.uu_id?.toString()) {
      showTooltip(t("incident_id_required"), "error");
      return;
    }
    if (!formState?.decision_type || formState?.decision_type === "pending") {
      showTooltip(t("decision_type_required"), "error");
      return;
    }
    if (!formState?.decision_date) {
      showTooltip(t("decision_date_required"), "error");
      return;
    }

    try {
      await editCrimeReferences2(incident?.uu_id, {
        decision_type: formState?.decision_type,
        decision_date: formState?.decision_date,
        decision_details: formState?.decision_details,
      });

      showTooltip(
        hasFinalDecision
          ? t("decision_updated_successfully")
          : t("decision_submitted_successfully"),
        "success",
      );
      setRefetch((prev) => !prev);
      setActiveForm(null);
    } catch (error: any) {
      showTooltip(
        error?.response?.data?.detail ||
          errorHandlerOnForm(error) ||
          errorHandler(error) ||
          t("something_went_wrong_try_again"),
        "error",
      );
    }
  };

  const handleDirectorAction = async (action: "reject" | "approve") => {
    setLoading(true);
    try {
      if (action === "reject") {
        await editCrimeReferences2(incident.uu_id, {
          screening_outcome: "rejected",
          decision_type: "pending",
          mesage_for_screeningteam:
            action === "reject" ? rejectReason : undefined,
        });
        showTooltip(t("successfully_rejected"), "success");
      } else {
        await editCrimeReferences2(incident.uu_id, {
          screening_outcome: "approved",
        });
        showTooltip(t("successfully_approved"), "success");
        setChangeTab("Screening");
      }
      setRefetch((prev) => !prev);
    } catch (error: any) {
      showTooltip(
        error?.response?.data?.detail || errorHandlerOnForm(error),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForwardToDirector = async () => {
    setLoading(true);
    try {
      await editCrimeReferences2(incident.uu_id, {
        screening_outcome: "forwarded_to_director",
      });
      showTooltip(t("successfully_forwarded_to_director"), "success");
      setRefetch((prev) => !prev);
    } catch (error: any) {
      showTooltip(
        error?.response?.data?.detail || errorHandlerOnForm(error),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const renderFindingForm = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              {hasFinding
                ? t("update_screening_finding")
                : t("add_screening_finding")}
            </p>
          </div>
        </div>
      </div>

      <FileUpload
        label={t("upload_finding_files")}
        fileTypes=".pdf,.jpg,.jpeg,.png"
        required={!hasFinding}
        info=""
        onChange={handleFile}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("description")}
        </label>
        <textarea
          rows={4}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
          name="description"
          value={formState?.description}
          onChange={handleChange}
          placeholder={t("enter_finding_description")}
        />
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => setActiveForm(null)}
          className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
        >
          {t("cancel")}
        </button>
        <button
          className={`flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors
            ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          disabled={loading}
          onClick={handleSubmitFinding}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2">
              {LoadingIcon}
              {t("uploading")}
            </div>
          ) : hasFinding ? (
            t("update_finding")
          ) : (
            t("upload_finding")
          )}
        </button>
      </div>
    </div>
  );

  const renderDecisionForm = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-purple-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-purple-700">
              {hasFinalDecision
                ? t("update_final_decision")
                : t("add_final_decision")}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("decision_type")} *
          </label>
          <select
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            name="decision_type"
            value={formState?.decision_type}
            onChange={handleChange}
          >
            <option value="pending">{t("select_decision_type")}</option>
            {(
              Object.keys(IncidentDecisionType) as Array<
                keyof typeof IncidentDecisionType
              >
            ).map((key) => (
              <option key={key} value={key}>
                {IncidentDecisionType[key]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("decision_date")} *
          </label>
          <input
            type="date"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            name="decision_date"
            value={formState?.decision_date}
            onChange={handleChange}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("decision_details")} *
          </label>
          <textarea
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            name="decision_details"
            value={formState?.decision_details}
            onChange={handleChange}
            placeholder={t("enter_decision_details")}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => setActiveForm(null)}
          className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
        >
          {t("cancel")}
        </button>
        <button
          className="flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          onClick={handleSubmitDecision}
        >
          {hasFinalDecision ? t("update_decision") : t("submit_decision")}
        </button>
      </div>
    </div>
  );

  const renderDecisionStatus = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-medium text-red-600 mb-2">
            {t("decision_type")}
          </p>
          <p className="font-semibold text-gray-900">
            {incident?.decision_type
              ? IncidentDecisionType[incident?.decision_type]
              : t("pending")}
          </p>
        </div>
        <div>
          <p className="text-sm font-medium text-red-600 mb-2">
            {t("decision_date")}
          </p>
          <p className="font-semibold text-gray-900">
            {incident?.decision_date
              ? new Date(incident?.decision_date)?.toDateString()
              : t("not_set")}
          </p>
        </div>
        <div className="md:col-span-2">
          <p className="text-sm font-medium text-red-600 mb-2">
            {t("decision_detail")}
          </p>
          <p className="text-gray-700">
            {incident?.decision_details?.toString() || t("no_details_provided")}
          </p>
        </div>

        {(incident?.screening_outcome === "rejected" ||
          incident?.screening_outcome === "approved") && (
          <div className="md:col-span-2">
            <p className="text-sm font-medium text-red-600 mb-2">
              {t("screening_outcome")}
            </p>
            <p className="font-semibold text-gray-900 capitalize">
              {incident?.screening_outcome?.toString()}
            </p>
          </div>
        )}
      </div>

      {incident?.decision_type?.toString() !== "pending" && (
        <div className="space-y-4">
          {hasRole(loginData?.roles, [Roles.prosecutionDirector]) &&
            incident?.screening_outcome === "forwarded_to_director" && (
              <div className="flex flex-col gap-4">
                {!isOpenReject && (
                  <div className="flex gap-3">
                    <button
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-white border border-amber-300 text-amber-700 font-medium rounded-lg hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                      onClick={() => setIsOpenReject(true)}
                    >
                      {loading ? (
                        <>
                          {LoadingIcon}
                          {t("loading")}
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4"
                            />
                          </svg>
                          {t("respond_back")}
                        </>
                      )}
                    </button>
                    <button
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                      onClick={() => handleDirectorAction("approve")}
                    >
                      {loading ? (
                        <>
                          {LoadingIcon}
                          {t("loading")}
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                          {t("approve")}
                        </>
                      )}
                    </button>
                  </div>
                )}
                {isOpenReject && (
                  <div className="flex flex-col gap-4">
                    <textarea
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      name="mesage_for_screeningteam"
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder={t("enter_reason")}
                    />
                    <div className="flex gap-3">
                      <button
                        className={`flex-1 px-4 py-3 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors
                          ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                        disabled={loading}
                        onClick={() => setIsOpenReject(false)}
                      >
                        {loading ? (
                          <div className="flex items-center justify-center gap-2">
                            {LoadingIcon}
                            {t("loading")}
                          </div>
                        ) : (
                          t("cancel")
                        )}
                      </button>
                      <button
                        className={`flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors
                    ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                        disabled={loading}
                        onClick={() => handleDirectorAction("reject")}
                      >
                        {loading ? (
                          <div className="flex items-center justify-center gap-2">
                            {LoadingIcon}
                            {t("loading")}
                          </div>
                        ) : (
                          t("respond_back")
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

          {hasRole(loginData?.roles, [Roles.screeningTeamCoordinator]) &&
            incident?.screening_outcome !== "approved" &&
            incident?.screening_outcome !== "forwarded_to_director" &&
            incident?.decision_type?.toString() !== "pending" && (
              <button
                className={`w-full px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors
                  ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={loading}
                onClick={handleForwardToDirector}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    {LoadingIcon}
                    {t("loading")}
                  </div>
                ) : (
                  t("forward_to_director")
                )}
              </button>
            )}
        </div>
      )}
    </div>
  );

  const outcome = incident?.screening_outcome?.toString();
  const screeningSteps = [
    { label: t("screening_finding"), done: hasFinding },
    { label: t("final_decision"), done: hasFinalDecision },
    {
      label: t("forward_to_director"),
      done:
        outcome === "forwarded_to_director" ||
        outcome === "approved" ||
        outcome === "rejected",
    },
    {
      label: t("screening_outcome"),
      done: outcome === "approved" || outcome === "rejected",
    },
  ];
  const currentStepIndex = screeningSteps.findIndex((s) => !s.done);

  const renderProgress = () => (
    <div className="flex items-start mb-8">
      {screeningSteps.map((step, index) => {
        const isActive = index === currentStepIndex;
        const isLast = index === screeningSteps.length - 1;
        return (
          <div
            key={index}
            className={`flex items-start ${isLast ? "" : "flex-1"}`}
          >
            <div className="flex flex-col items-center">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors ${
                  step.done
                    ? "bg-green-600 border-green-600 text-white"
                    : isActive
                      ? "bg-white border-red-600 text-red-600"
                      : "bg-white border-gray-300 text-gray-400"
                }`}
              >
                {step.done ? "✓" : index + 1}
              </div>
              <span
                className={`mt-2 text-xs text-center w-[88px] leading-tight ${
                  step.done || isActive
                    ? "text-gray-900 font-medium"
                    : "text-gray-400"
                }`}
              >
                {step.label}
              </span>
            </div>
            {!isLast && (
              <div
                className={`flex-1 h-0.5 mt-[18px] mx-1 rounded ${
                  step.done ? "bg-green-600" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Current user */}
      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
        <div className="w-10 h-10 shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
          <span className="text-gray-600">👤</span>
        </div>
        <div>
          <p className="font-semibold text-gray-900">{loginData?.full_name}</p>
          <p className="text-sm text-gray-600">
            {loginData?.roles?.map((role) => splitCamelCase(role)).join(", ")}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 mb-6">
        <div className="w-11 h-11 shrink-0 bg-red-50 rounded-xl flex items-center justify-center">
          <span className="text-xl">🔎</span>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t("screening_finding")}
          </h2>
          <p className="text-sm text-gray-600 mt-0.5">
            {t("manage_screening_findings_and_final_decisions")}
          </p>
        </div>
      </div>

      {/* Workflow progress */}
      {renderProgress()}

      {/* Director's message to the screening team */}
      {incident?.mesage_for_screeningteam && (
        <div className="mb-8 border-l-4 border-amber-500 bg-amber-50 rounded-r-lg p-4">
          <p className="text-sm font-semibold text-amber-800 mb-1">
            {t("director_message")}
          </p>
          <p className="text-sm text-amber-900 whitespace-pre-line">
            {incident?.mesage_for_screeningteam}
          </p>
        </div>
      )}

      {activeForm ? (
        activeForm === "finding" ? (
          renderFindingForm()
        ) : (
          renderDecisionForm()
        )
      ) : (
        <>
          {/* Screening team that was assigned to this incident */}
          {incident?.screening_members &&
            incident?.screening_members.length > 0 && (
              <div className="mb-8">
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  {t("screening_team")}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {incident.screening_members.map((member, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-sm text-gray-800"
                    >
                      <span>👤</span>
                      {member?.prosecutor_name}
                      {member?.team_leader && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          {t("team_lead")}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

          {/* Documented screening findings (collapsible accordion) */}
          {hasFinding && (
            <div className="mb-8">
              <h3 className="text-base font-semibold text-gray-900 mb-3">
                {t("screening_findings")}
              </h3>
              <div className="space-y-2">
                {(incident?.screening_team_findings || []).map(
                  (finding, index, arr) => {
                    // Default the most recent finding open until the user
                    // interacts; -1 means the user collapsed everything.
                    const activeIndex =
                      openFindingIndex === null
                        ? arr.length - 1
                        : openFindingIndex;
                    const isOpen = activeIndex === index;
                    return (
                      <div
                        key={index}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setOpenFindingIndex(isOpen ? -1 : index)
                          }
                          className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                        >
                          <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            <span className="w-6 h-6 shrink-0 rounded-full bg-gray-200 text-gray-700 text-xs flex items-center justify-center">
                              {index + 1}
                            </span>
                            {t("screening_finding")} {index + 1}
                          </span>
                          <span className="flex items-center gap-3 shrink-0">
                            {finding?.uploaded_at && (
                              <span className="text-xs text-gray-500">
                                {new Date(
                                  finding.uploaded_at,
                                ).toLocaleDateString()}
                              </span>
                            )}
                            <svg
                              className={`w-4 h-4 text-gray-500 transition-transform ${
                                isOpen ? "rotate-180" : ""
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-4 py-4 border-t border-gray-200 bg-white">
                            {finding?.description && (
                              <p className="text-sm text-gray-700 mb-3">
                                {finding?.description}
                              </p>
                            )}
                            <DocumentViewerModal
                              documents={(finding?.documents || []) as any}
                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}${FILE_URL.SCREENING}`}
                              layout="list"
                              hideEmptyFile={true}
                            />
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}

          {/* Action Cards */}
          {!hasFinalDecision &&
            hasRole(loginData?.roles, [Roles.screeningTeamCoordinator]) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Finding Card */}
                <div
                  className={`border-2 rounded-xl p-6 transition-all flex flex-col ${
                    hasFinding
                      ? "border-blue-200 bg-blue-50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  {/* Icon */}
                  <div className="flex items-center justify-center mb-4">
                    <div
                      className={`p-3 rounded-lg ${
                        hasFinding ? "bg-blue-100" : "bg-gray-100"
                      }`}
                    >
                      <svg
                        className={`w-8 h-8 ${
                          hasFinding ? "text-blue-600" : "text-gray-600"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 text-center mb-2">
                    {hasFinding ? t("screening_finding") : t("add_finding")}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 text-center mb-4">
                    {hasFinding
                      ? t("screening_finding_documented")
                      : t("upload_screening_files_and_add_description")}
                  </p>

                  {/* Status */}
                  <div className="flex justify-center mb-6">
                    <span
                      className={`px-3 py-1 text-xs rounded-full ${
                        hasFinding
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {hasFinding ? t("added") : t("required")}
                    </span>
                  </div>

                  {/* Button */}
                  <button
                    onClick={() => setActiveForm("finding")}
                    className={`w-full px-4 py-3 font-medium rounded-lg transition-colors ${
                      hasFinding
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-600 text-white hover:bg-gray-700"
                    }`}
                  >
                    {hasFinding ? t("update_finding") : t("add_finding")}
                  </button>
                </div>

                {/* Decision Card */}
                <div
                  className={`border-2 rounded-xl p-6 transition-all flex flex-col ${
                    hasFinalDecision
                      ? "border-purple-200 bg-purple-50"
                      : hasFinding
                        ? "border-red-200 bg-red-50"
                        : "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed"
                  }`}
                >
                  {/* Icon */}
                  <div className="flex items-center justify-center mb-4">
                    <div
                      className={`p-3 rounded-lg ${
                        hasFinalDecision
                          ? "bg-purple-100"
                          : hasFinding
                            ? "bg-red-100"
                            : "bg-gray-100"
                      }`}
                    >
                      <svg
                        className={`w-8 h-8 ${
                          hasFinalDecision
                            ? "text-purple-600"
                            : hasFinding
                              ? "text-red-600"
                              : "text-gray-600"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 text-center mb-2">
                    {hasFinalDecision
                      ? t("final_decision")
                      : t("make_decision")}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 text-center mb-4">
                    {hasFinalDecision
                      ? t("final_screening_decision_made")
                      : hasFinding
                        ? t("add_final_screening_decision")
                        : t("requires_screening_finding_first")}
                  </p>

                  {/* Status */}
                  <div className="flex justify-center mb-6">
                    <span
                      className={`px-3 py-1 text-xs rounded-full ${
                        hasFinalDecision
                          ? "bg-purple-100 text-purple-800"
                          : hasFinding
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {hasFinalDecision
                        ? t("completed")
                        : hasFinding
                          ? t("available")
                          : t("locked")}
                    </span>
                  </div>

                  {/* Button */}
                  <button
                    onClick={() => hasFinding && setActiveForm("decision")}
                    disabled={!hasFinding}
                    className={`w-full px-4 py-3 font-medium rounded-lg transition-colors ${
                      hasFinding
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    {hasFinalDecision
                      ? t("update_decision")
                      : t("make_decision")}
                  </button>
                </div>
              </div>
            )}

          {/* Show decision status if there's a decision */}
          {hasFinalDecision && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t("current_decision_status")}
              </h3>
              {renderDecisionStatus()}
            </div>
          )}

          {/* Show empty state if no finding (the coordinator already has the
              finding action card above, so avoid showing a duplicate CTA) */}
          {!hasFinding &&
            !hasRole(loginData?.roles, [Roles.screeningTeamCoordinator]) && (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-xl">
              <div className="text-gray-400 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t("no_screening_finding_added")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("add_screening_finding_to_proceed")}
              </p>
              <button
                onClick={() => setActiveForm("finding")}
                className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                {t("add_screening_finding")}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CrimeFinalInformation;
